import React, { useContext, useEffect, useState, useRef, useMemo } from "react";
import { Helmet } from "react-helmet-async";

import { DataContext } from "../context/contextApi";
import LazyImage from "../components/LazyImage";
import VideoCard from "../components/VideoCard";
import RecommendedRow from "../components/RecommendedRow";
import PlatformNoticeModal from "../components/PlatformNoticeModal";
import UseOnline from "../utils/UseOnline";
import OfflineComponent from "../shared/OfflineComponent";
import ShimmerVideoCard from "../shared/ShimmerVideoCard";
import pndBanner from "../assets/d33c4227-8938-4ea6-a2f5-065528230c96.png";
import { PROMO_BANNER_ENABLED } from "../utils/constants";

const Feed = () => {
  const { loading, searchResults, firebaseVideos, firebaseLoading, localVideos } = useContext(DataContext);
  // console.log("from feed", searchResults);

  const isOnline = UseOnline();
  const [showPlatformNotice, setShowPlatformNotice] = useState(false);

  useEffect(() => {
    document.getElementById("root").classList.remove("custom-h");

    try {
      const seen = localStorage.getItem("platform_notice_seen");
      if (!seen) setShowPlatformNotice(true);
    } catch (e) {}
  }, []);

  const handleClosePlatformNotice = () => {
    try {
      localStorage.setItem("platform_notice_seen", "1");
    } catch (e) {}
    setShowPlatformNotice(false);
  };

  const top10Ref = useRef(null);

  const scrollTop10 = (dir) => {
    if (!top10Ref.current) return;
    const el = top10Ref.current;
    const amount = Math.round(el.clientWidth * 0.9) || el.clientWidth || 300;
    el.scrollBy({ left: dir === "left" ? -amount : amount, behavior: "smooth" });
  };

  // deduped union of DB videos + local JSON (used by the recommendation shelf)
  const allVideos = useMemo(() => {
    const map = new Map();
    (Array.isArray(firebaseVideos) ? firebaseVideos : []).forEach((v) => {
      if (v && v.videoId) map.set(v.videoId, v);
    });
    (Array.isArray(localVideos) ? localVideos : []).forEach((v) => {
      if (v && v.videoId && !map.has(v.videoId)) map.set(v.videoId, v);
    });
    return Array.from(map.values());
  }, [firebaseVideos, localVideos]);

  return (
    <div className="flex flex-1">

      {!isOnline && (
        <div className="grow w-full h-full overflow-y-auto bg-black">
          <OfflineComponent />
        </div>
      )}
      {isOnline && (
        <div className="grow w-full h-full overflow-y-auto bg-white dark:bg-black">
          <Helmet>
            <title>Anime Forever Tamil - Home</title>
            <meta name="description" content="Browse anime and cartoon videos in Tamil. Anime Forever Tamil aggregates public videos from YouTube and other platforms." />
            <link rel="canonical" href={window.location.origin + window.location.pathname} />
          </Helmet>
          {showPlatformNotice && (
            <PlatformNoticeModal onClose={handleClosePlatformNotice} />
          )}

          {/* Recommended / suggestions - personalized from local view history (random for new users) */}
          <RecommendedRow videos={allVideos} loading={loading || firebaseLoading} />

          {/* Top 10 Popular - horizontal scrollable section showing 2 thumbnails per view */}
          <div className="px-5 pb-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold text-black dark:text-white">Top 10 Popular</h2>
            </div>

            <div className="relative -mx-2">
              <div
                ref={top10Ref}
                className="overflow-x-auto -mx-2 px-2 scroll-smooth snap-x snap-mandatory show-scrollbar"
                aria-label="Top 10 popular videos"
              >
                <div className="flex space-x-4">
                  {(() => {
                    // show shimmer placeholders while Firebase or API data is loading
                    if (loading || firebaseLoading) {
                      return Array(4)
                        .fill("")
                        .map((_, idx) => (
                          <div key={`shimmer-top10-${idx}`} className="flex-shrink-0 min-w-[50%] max-w-[50%] px-2 snap-start">
                            <ShimmerVideoCard />
                          </div>
                        ));
                    }

                    const map = new Map();
                    if (Array.isArray(firebaseVideos)) {
                      firebaseVideos.forEach((v) => {
                        if (v && v.videoId) map.set(v.videoId, v);
                      });
                    }
                    if (Array.isArray(localVideos)) {
                      localVideos.forEach((v) => {
                        if (v && v.videoId && !map.has(v.videoId)) map.set(v.videoId, v);
                      });
                    }

                    const combined = Array.from(map.values())
                      .sort((a, b) => ((b?.stats?.views || 0) - (a?.stats?.views || 0)))
                      .slice(0, 10);

                    if (combined.length === 0) return null;

                    return combined.map((video) => (
                      <div key={video.videoId} className="flex-shrink-0 min-w-[50%] max-w-[50%] px-2 snap-start">
                        <VideoCard video={video} />
                      </div>
                    ));
                  })()}
                </div>
              </div>

              {/* arrows - visible on md+ */}
              <button
                type="button"
                onClick={() => scrollTop10('left')}
                className="hidden md:flex items-center justify-center absolute left-1 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white/90 dark:bg-black/60 shadow-md"
                aria-label="Scroll left"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 18L9 12L15 6" stroke="#111827" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              <button
                type="button"
                onClick={() => scrollTop10('right')}
                className="hidden md:flex items-center justify-center absolute right-1 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white/90 dark:bg-black/60 shadow-md"
                aria-label="Scroll right"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18L15 12L9 6" stroke="#111827" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-5">
            {/* Show shimmer when either API data or Firebase data is loading */}
            {(loading || firebaseLoading) ? (
              Array(24)
                .fill("")
                .map((e, index) => {
                  return <ShimmerVideoCard key={index} />;
                })
            ) : (
              (() => {
                // Build and memoize a shuffled list of unique videos when inputs change
                const videosToRender = (() => {
                  const map = new Map();

                  if (Array.isArray(firebaseVideos)) {
                    firebaseVideos.forEach((v) => {
                      if (v && v.videoId) map.set(v.videoId, v);
                    });
                  }

                  if (Array.isArray(searchResults)) {
                    searchResults.forEach((item) => {
                      if (item?.type !== "video") return;
                      const v = item.video;
                      if (v && v.videoId && !map.has(v.videoId)) map.set(v.videoId, v);
                    });
                  }

                  const arr = Array.from(map.values());

                  // Fisher-Yates shuffle
                  for (let i = arr.length - 1; i > 0; i--) {
                    const j = Math.floor(Math.random() * (i + 1));
                    [arr[i], arr[j]] = [arr[j], arr[i]];
                  }
                  return arr;
                })();

                // Inject banner images randomly between VideoCards. We'll use a per-gap probability
                // so banners appear in different positions on every render. Ensure at least one banner
                // is inserted if there are videos. (Banner hidden until PROMO_BANNER_ENABLED is true.)
                const items = [];
                const bannerProb = 0.2; // 20% chance to insert a banner after each video
                let bannerInserted = false;

                videosToRender.forEach((video, i) => {
                  items.push(<VideoCard key={video.videoId || `v-${i}`} video={video} />);

                  // decide whether to insert a banner after this video
                  if (PROMO_BANNER_ENABLED && Math.random() < bannerProb) {
                    bannerInserted = true;
                    items.push(
                      <div key={`banner-${i}`} className="w-full flex justify-center">
                        <a href="https://t.me/mywavelength_bot" target="_blank" rel="noopener noreferrer" className="w-full max-w-sm">
                          <LazyImage src={pndBanner} alt="promo banner" className="w-full object-contain rounded-md shadow-sm cursor-pointer" />
                        </a>
                      </div>
                    );
                  }
                });

                // If no banner was inserted, insert one at a random position (not at index 0)
                if (PROMO_BANNER_ENABLED && !bannerInserted && items.length > 0) {
                  const insertAfter = Math.min(items.length - 1, Math.max(0, Math.floor(Math.random() * items.length)));
                  items.splice(insertAfter + 1, 0,
                    <div key={`banner-fallback`} className="w-full flex justify-center">
                      <a href="https://t.me/mywavelength_bot" target="_blank" rel="noopener noreferrer" className="w-full max-w-sm">
                        <LazyImage src={pndBanner} alt="promo banner" className="w-full object-contain rounded-md shadow-sm cursor-pointer" />
                      </a>
                    </div>
                  );
                }

                return items;
              })()
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Feed;
